import { EVENT_DATES_MAX_OPTIONS, EVENT_DATES_MAX_SPAN_DAYS, ONE_DAY_MS } from "./constants";
import type { EventDateOption } from "@/lib/utils/event-dates";

export const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Add N days to a `YYYY-MM-DD` string in UTC. Returns null on malformed
 * input so consumers can skip rather than silently fabricate a date.
 *
 * Wall-clock UTC math throughout the rooms / agenda date plumbing — never
 * construct a local Date from these strings, or DST boundaries will shift
 * persisted dates by an hour and the wrong day pops out.
 *
 * @param date - `YYYY-MM-DD` string.
 * @param offset - Days to add (can be negative).
 * @returns Resulting `YYYY-MM-DD`, or null when input is malformed.
 */
export function addDays(date: string, offset: number): string | null {
  if (!YYYY_MM_DD.test(date)) return null;
  const t = Date.parse(`${date}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  return new Date(t + offset * ONE_DAY_MS).toISOString().slice(0, 10);
}

/**
 * Days in an inclusive `YYYY-MM-DD` range. Returns 0 for malformed or
 * inverted input — callers treat that as "skip this range".
 *
 * @param startDate - Inclusive start.
 * @param endDate - Inclusive end.
 * @returns Day count, or 0 on bad input.
 */
export function rangeLengthInDays(startDate: string, endDate: string): number {
  if (!YYYY_MM_DD.test(startDate) || !YYYY_MM_DD.test(endDate)) return 0;
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 0;
  return Math.floor((end - start) / ONE_DAY_MS) + 1;
}

/**
 * Re-narrows client-supplied ranked date options at a write boundary — same shape/cap pattern
 * as `serializeAgendaDateRanges`, applied to `events.event_dates`. Order is preserved (rank =
 * array position); a malformed entry is dropped in place rather than shifting later ranks.
 * Shared by the event-details editor's `updateEventDates` action and the create-event-v3
 * wizard's final-insert mapping, so the two writers of this column can't drift.
 *
 * @param options - Raw payload from the client.
 * @returns Sanitized, ranked options ready to write.
 */
export function serializeEventDateOptions(options: EventDateOption[]): EventDateOption[] {
  if (!Array.isArray(options)) return [];
  const out: EventDateOption[] = [];
  for (const o of options) {
    if (out.length >= EVENT_DATES_MAX_OPTIONS) break;
    if (!o || typeof o !== "object") continue;
    const raw = o as unknown as Record<string, unknown>;
    if (typeof raw.startDate === "string" && typeof raw.endDate === "string") {
      const spanDays = rangeLengthInDays(raw.startDate, raw.endDate);
      if (spanDays === 0 || spanDays > EVENT_DATES_MAX_SPAN_DAYS) continue;
      out.push({ startDate: raw.startDate, endDate: raw.endDate });
      continue;
    }
    if (typeof raw.date !== "string" || rangeLengthInDays(raw.date, raw.date) !== 1) continue;
    if (!validOptionalTime(raw.startTime) || !validOptionalTime(raw.endTime)) continue;
    out.push({
      date: raw.date,
      ...(typeof raw.startTime === "string" ? { startTime: raw.startTime } : {}),
      ...(typeof raw.endTime === "string" ? { endTime: raw.endTime } : {}),
    });
  }
  return out;
}

/** Whether an optional event time is a 24-hour `HH:MM` string. */
function validOptionalTime(value: unknown): boolean {
  return value === undefined || (typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value));
}

/**
 * Build the set of YYYY-MM-DD dates covered (inclusively) by a list of ranges.
 * Caller MUST pre-validate ranges (e.g. via a bounded-size/span filter) so this
 * loop is bounded — it iterates every day in every range with no cap of its own.
 *
 * @param ranges - Inclusive `{startDate, endDate}` ranges.
 * @returns The union of all dates covered, as a Set.
 */
export function buildDateUnion(ranges: ReadonlyArray<{ startDate: string; endDate: string }>): Set<string> {
  const set = new Set<string>();
  for (const range of ranges) {
    const start = Date.parse(`${range.startDate}T00:00:00Z`);
    const end = Date.parse(`${range.endDate}T00:00:00Z`);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) continue;
    for (let t = start; t <= end; t += ONE_DAY_MS) {
      set.add(new Date(t).toISOString().slice(0, 10));
    }
  }
  return set;
}

/**
 * The days the event details page offers for meeting-space schedules and food & beverage: the
 * agenda's range days plus the event's preferred date option, the days the create-event wizard
 * offers. A preferred range longer than the write-time span cap adds nothing rather than an
 * unbounded list.
 *
 * @param eventDates - Ranked `events.event_dates`; index 0 is preferred.
 * @param agendaRanges - The agenda's date ranges (`events.meeting_room_dates`).
 * @returns Sorted, de-duplicated `YYYY-MM-DD` days; empty when neither is set.
 */
export function eventDayOptions(
  eventDates: EventDateOption[],
  agendaRanges: ReadonlyArray<{ startDate: string; endDate: string }>
): string[] {
  const days = buildDateUnion(agendaRanges);
  const option = eventDates[0];
  if (option?.startDate && option.endDate) {
    const span = rangeLengthInDays(option.startDate, option.endDate);
    if (span > 0 && span <= EVENT_DATES_MAX_SPAN_DAYS) {
      buildDateUnion([{ startDate: option.startDate, endDate: option.endDate }]).forEach((d) => days.add(d));
    }
  } else if (option?.date && YYYY_MM_DD.test(option.date)) {
    days.add(option.date);
  }
  return Array.from(days).sort();
}
